import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  IconButton,
  CircularProgress,
  Autocomplete,
  TablePagination,
  Switch,
  FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { FileUploadScheduler } from '../models/FileUploadScheduler';
import { fileUploadSchedulerService } from '../services/api/fileUploadSchedulerService';
import { ftpService } from '../services/api/ftpService';
import { Ftp } from '../types/api.types';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusPopup from '../components/StatusPopup';
import LoadingOverlay from '../components/LoadingOverlay';

const SCHEDULE_TYPES = [
  { value: 'ONE_TIME', label: 'One Time' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

const WEEKDAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const MONTH_DATES = Array.from({ length: 31 }, (_, i) => i + 1);

type SchedulerForm = {
  name: string;
  status: boolean;
  fileType: 'csv' | 'text';
  scheduleType: 'ONE_TIME' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  recurrenceInterval: number;
  startDatetime: string;
  weekDays: number[];
  monthDates: number[];
};

const UploadFileScheduler: React.FC = () => {
  const [rows, setRows] = useState<FileUploadScheduler[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<SchedulerForm>({
    name: '',
    status: true,
    fileType: 'csv',
    scheduleType: 'ONE_TIME',
    recurrenceInterval: 1,
    startDatetime: '',
    weekDays: [],
    monthDates: [],
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [ftps, setFtps] = useState<Ftp[]>([]);
  const [selectedFtp, setSelectedFtp] = useState<Ftp | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; name: string } | null>(null);
  const [statusPopup, setStatusPopup] = useState({ open: false, success: false, message: '' });

  const fetchRows = async (keyword: string = '', currentPage: number = 0, currentPageSize: number = 10) => {
    setLoading(true);
    try {
      const res = await fileUploadSchedulerService.getAll(keyword, currentPage + 1, currentPageSize);
      setRows(res.data);
      setTotal(res.total);
    } catch (e: any) {
      setStatusPopup({ open: true, success: false, message: e.message || 'Failed to fetch schedulers' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows(searchQuery, page, rowsPerPage);
    // eslint-disable-next-line
  }, [searchQuery, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    console.log('Search clicked', searchInput);
    setPage(0);
    setSearchQuery(searchInput);
    fetchRows(searchInput, 0, rowsPerPage);
  };

  const handleOpenDialog = async () => {
    setDialogOpen(true);
    setDialogLoading(true);
    try {
      const res = await ftpService.getAllFtps('', 1, 1000);
      setFtps(res.data);
    } catch (e: any) {
      setStatusPopup({ open: true, success: false, message: e.message || 'Failed to fetch FTP servers' });
    }
    setDialogLoading(false);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setForm({ name: '', status: true, fileType: 'csv', scheduleType: 'ONE_TIME', recurrenceInterval: 1, startDatetime: '', weekDays: [], monthDates: [] });
    setSelectedFtp(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name!]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, status: e.target.checked }));
  };

  const handleScheduleTypeChange = (e: SelectChangeEvent) => {
    const scheduleType = e.target.value as SchedulerForm['scheduleType'];
    setForm(prev => ({
      ...prev,
      scheduleType,
      recurrenceInterval: scheduleType === 'ONE_TIME' ? 1 : (prev.recurrenceInterval > 0 ? prev.recurrenceInterval : 1),
      weekDays: scheduleType === 'WEEKLY' ? prev.weekDays : [],
      monthDates: scheduleType === 'MONTHLY' ? prev.monthDates : [],
    }));
  };

  const handleFileTypeChange = (e: SelectChangeEvent) => {
    setForm(prev => ({ ...prev, fileType: e.target.value as 'csv' | 'text' }));
  };

  const handleStartDatetimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, startDatetime: e.target.value }));
  };

  const handleRecurrenceIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 1) value = 1;
    setForm(prev => ({ ...prev, recurrenceInterval: value }));
  };

  const handleWeekDayChange = (dayValue: number) => {
    setForm(prev => ({
      ...prev,
      weekDays: prev.weekDays.includes(dayValue)
        ? prev.weekDays.filter(day => day !== dayValue)
        : [...prev.weekDays, dayValue].sort()
    }));
  };

  const handleMonthDateChange = (dateValue: number) => {
    setForm(prev => ({
      ...prev,
      monthDates: prev.monthDates.includes(dateValue)
        ? prev.monthDates.filter(date => date !== dateValue)
        : [...prev.monthDates, dateValue].sort((a, b) => a - b)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFtp) {
      setStatusPopup({ open: true, success: false, message: 'Please select an FTP server' });
      return;
    }
    if (!form.name || !form.startDatetime) {
      setStatusPopup({ open: true, success: false, message: 'Please fill all required fields' });
      return;
    }
    if (form.scheduleType !== 'ONE_TIME' && (!form.recurrenceInterval || form.recurrenceInterval < 1)) {
      setStatusPopup({ open: true, success: false, message: 'Recurrence interval must be at least 1' });
      return;
    }
    if (form.scheduleType === 'WEEKLY' && form.weekDays.length === 0) {
      setStatusPopup({ open: true, success: false, message: 'Please select at least one weekday for weekly schedule' });
      return;
    }
    if (form.scheduleType === 'MONTHLY' && form.monthDates.length === 0) {
      setStatusPopup({ open: true, success: false, message: 'Please select at least one date for monthly schedule' });
      return;
    }
    setLoading(true);
    try {
      await fileUploadSchedulerService.create({
        name: form.name,
        status: form.status,
        fileType: form.fileType,
        scheduleType: form.scheduleType,
        recurrenceInterval: form.scheduleType === 'ONE_TIME' ? null : Number(form.recurrenceInterval || 1),
        startDatetime: form.startDatetime,
        ftpId: selectedFtp.id,
        weekDays: form.scheduleType === 'WEEKLY' ? form.weekDays : undefined,
        monthDates: form.scheduleType === 'MONTHLY' ? form.monthDates : undefined,
      });
      setStatusPopup({ open: true, success: true, message: 'Schedule created successfully' });
      handleCloseDialog();
      fetchRows(searchQuery, page, rowsPerPage);
    } catch (e: any) {
      setStatusPopup({ open: true, success: false, message: e.message || 'Failed to create schedule' });
    }
    setLoading(false);
  };

  const showStatusPopup = (success: boolean, message: string) => {
    setStatusPopup({ open: true, success, message });
  };

  const handleStatusPopupClose = () => {
    setStatusPopup(prev => ({ ...prev, open: false }));
  };

  const getRecurrenceIntervalUnit = (scheduleType: string) => {
    switch (scheduleType) {
      case 'DAILY':
        return 'days';
      case 'WEEKLY':
        return 'weeks';
      case 'MONTHLY':
        return 'months';
      default:
        return '';
    }
  };

  // Table columns: Name, Status, Schedule Type, File Type, Start Date/Time, Recurrence Interval, Last Run, Next Run, Created Date, Actions
  return (
    <Box sx={{
      bgcolor: 'white',
      borderRadius: 2,
      p: { xs: 2, sm: 3 },
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      minHeight: 'calc(100vh - 120px)'
    }}>
      <Box sx={{ p: 0 }}>
        <LoadingOverlay open={loading} />
        {/* Header Section */}
        <Box sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Upload File Scheduler
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Schedule and manage your file uploads to FTP servers
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenDialog}
              sx={{
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
              }}
            >
              Add Schedule
            </Button>
          </Box>
        </Box>
        {/* Search Section */}
        <Box sx={{
          mb: 3,
          p: 3,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <TextField
            size="small"
            placeholder="Search schedules..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            sx={{
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Search
          </Button>
        </Box>
        {/* Table Section */}
        <Box sx={{
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Schedule Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>File Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Start Date/Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Recurrence Interval</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Run</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Next Run</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No schedules found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}
                      sx={{
                        '&:hover': { bgcolor: '#f8f9fa' },
                        '&:nth-of-type(even)': { bgcolor: '#fafafa' }
                      }}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: row.status ? '#e8f5e8' : '#ffebee',
                          color: row.status ? '#2e7d32' : '#c62828',
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}>
                          {row.status ? 'Enable' : 'Disable'}
                        </Box>
                      </TableCell>
                      <TableCell>{SCHEDULE_TYPES.find(t => t.value === row.scheduleType)?.label || row.scheduleType}</TableCell>
                      <TableCell>{row.fileType.toUpperCase()}</TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: '150px' }}>{row.startDatetime?.replace('T', ' ').slice(0, 16)}</Box>
                      </TableCell>
                      <TableCell>{row.scheduleType === 'ONE_TIME' ? '-' : row.recurrenceInterval + ' ' + getRecurrenceIntervalUnit(row.scheduleType)}</TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: '150px' }}>{row.lastRun ? row.lastRun.replace('T', ' ').slice(0, 16) : '-'}</Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: '150px' }}>{row.nextRun ? row.nextRun.replace('T', ' ').slice(0, 16) : '-'}</Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: '150px' }}>{row.createdAt?.replace('T', ' ').slice(0, 16)}</Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setItemToDelete({ id: row.id, name: row.name });
                            setConfirmDeleteOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid #e0e0e0',
              bgcolor: '#f8f9fa'
            }}
          />
        </Box>
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle>Add File Upload Schedule</DialogTitle>
            <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dialogLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TextField
                    name="name"
                    label="Name"
                    value={form.name}
                    onChange={handleFormChange}
                    required
                    fullWidth
                  />
                  <FormControlLabel
                    control={<Switch checked={form.status} onChange={handleSwitchChange} color="primary" />}
                    label={form.status ? 'Enable' : 'Disable'}
                    sx={{ mb: 1 }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>File Type</InputLabel>
                    <Select
                      name="fileType"
                      value={form.fileType}
                      label="File Type"
                      onChange={handleFileTypeChange}
                      required
                    >
                      <MenuItem value="csv">CSV</MenuItem>
                      <MenuItem value="text">Text</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Schedule Type</InputLabel>
                    <Select
                      name="scheduleType"
                      value={form.scheduleType}
                      label="Schedule Type"
                      onChange={handleScheduleTypeChange}
                      required
                    >
                      {SCHEDULE_TYPES.map(type => (
                        <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {(form.scheduleType !== 'ONE_TIME') && (
                    <TextField
                      name="recurrenceInterval"
                      label={`Recur every X ${form.scheduleType === 'DAILY' ? 'days' : form.scheduleType === 'WEEKLY' ? 'weeks' : 'months'}`}
                      type="number"
                      inputProps={{ min: 1 }}
                      value={String(form.recurrenceInterval)}
                      onChange={handleRecurrenceIntervalChange}
                      required
                      fullWidth
                    />
                  )}
                  {form.scheduleType === 'WEEKLY' && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Select weekdays
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {WEEKDAYS.map((day) => (
                          <FormControlLabel
                            key={day.value}
                            control={
                              <Switch
                                checked={form.weekDays.includes(day.value)}
                                onChange={() => handleWeekDayChange(day.value)}
                                size="small"
                              />
                            }
                            label={day.label}
                            sx={{
                              minWidth: 'fit-content',
                              '& .MuiFormControlLabel-label': { fontSize: '0.875rem' }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {form.scheduleType === 'MONTHLY' && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Select dates
                      </Typography>
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
                        gap: 1,
                        maxHeight: 200,
                        overflowY: 'auto',
                        p: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1
                      }}>
                        {MONTH_DATES.map((date) => (
                          <FormControlLabel
                            key={date}
                            control={
                              <Switch
                                checked={form.monthDates.includes(date)}
                                onChange={() => handleMonthDateChange(date)}
                                size="small"
                              />
                            }
                            label={date.toString()}
                            sx={{
                              minWidth: 'fit-content',
                              '& .MuiFormControlLabel-label': { fontSize: '0.875rem' }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  <TextField
                    name="startDatetime"
                    label="Start Date/Time"
                    type="datetime-local"
                    value={form.startDatetime}
                    onChange={handleStartDatetimeChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <Autocomplete
                      options={ftps}
                      getOptionLabel={(option) => `${option.host}:${option.port}`}
                      value={selectedFtp}
                      onChange={(_, newValue) => setSelectedFtp(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select FTP Server"
                          required
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      )}
                    />
                  </FormControl>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} variant="outlined"
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                }}
              >Cancel</Button>
              <Button type="submit" variant="contained" color="primary"
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                }}
                disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        <ConfirmDialog
          open={confirmDeleteOpen}
          title="Delete Schedule"
          message={`Are you sure you want to delete schedule${itemToDelete ? ` "${itemToDelete.name}"` : ''}?`}
          onConfirm={async () => {
            if (itemToDelete) {
              setLoading(true);
              try {
                await fileUploadSchedulerService.delete(itemToDelete.id);
                showStatusPopup(true, 'Schedule deleted successfully!');
                fetchRows(searchQuery, page, rowsPerPage);
              } catch (e: any) {
                showStatusPopup(false, e.message || 'Failed to delete schedule');
              }
              setLoading(false);
              setConfirmDeleteOpen(false);
              setItemToDelete(null);
            }
          }}
          onCancel={() => {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
          }}
        />
        <StatusPopup
          open={statusPopup.open}
          success={statusPopup.success}
          message={statusPopup.message}
          onClose={handleStatusPopupClose}
        />
      </Box>
    </Box>
  );
};

export default UploadFileScheduler; 